import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { StoryArcSurface } from "@/components/match/StoryArcSurface";
import { NationRivalryMomentsSurface } from "@/components/social/NationRivalryMomentsSurface";
import { WorldStatusSurface } from "@/components/global/WorldStatusSurfaces";
import { getNationRivalry } from "@/lib/social.functions";
import { usePublicEmotionalSignal, type PublicEmotionalCtx } from "@/hooks/usePublicEmotionalSignal";
import { trackEvent } from "@/lib/audit";

type Slot = "story_arc" | "nation_rivalry" | "world_status";

/**
 * EmotionalRibbonOrchestrator
 * --------------------------
 * Single slot that rotates Story Arc, Nation Rivalry and World Status.
 * Picks the dominant ribbon by priority (live story arc > strong storm >
 * world status), and falls back to a soft rotation every 10s when several
 * are equally relevant. Never renders more than one at a time. No new
 * polling — reuses the same React-Query keys already in use.
 */
export function EmotionalRibbonOrchestrator({
  ctx,
  minute,
  isLive,
  className = "",
}: {
  ctx: PublicEmotionalCtx;
  minute: number;
  isLive: boolean;
  className?: string;
}) {
  const sig = usePublicEmotionalSignal(ctx);
  const rivalry = useQuery({
    queryKey: ["social", "nation_rivalry"],
    queryFn: () => getNationRivalry(),
    refetchInterval: 30_000,
    staleTime: 20_000,
  });

  const rows = (rivalry.data?.rows ?? []) as Array<{ nation_code: string; hype_total: number; active_fans: number }>;
  const topTotal = useMemo(
    () => rows.reduce((m, r) => Math.max(m, Number(r.hype_total ?? 0)), 0),
    [rows],
  );

  const candidates = useMemo<Slot[]>(() => {
    const out: Slot[] = [];
    const arcStrong =
      isLive &&
      minute > 0 &&
      (sig.intensity >= 0.45 ||
        sig.arc.phase === "comeback" ||
        sig.arc.phase === "collapse" ||
        sig.arc.phase === "desperation" ||
        sig.arc.phase === "resistance");
    if (arcStrong) out.push("story_arc");
    if (topTotal >= 200) out.push("nation_rivalry");
    if (rows.length > 0) out.push("world_status");
    if (out.length === 0 && isLive && minute > 0) out.push("story_arc");
    return out;
  }, [isLive, minute, sig.intensity, sig.arc.phase, topTotal, rows.length]);

  // Soft rotation: every 10s, advance the index across the candidate list.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setTick((x) => x + 1), 10_000);
    return () => window.clearInterval(id);
  }, []);

  const active: Slot | null = candidates.length > 0
    ? candidates[tick % candidates.length]
    : null;

  useEffect(() => {
    if (!active) return;
    void trackEvent("emotional_ribbon_rotated", { slot: active, candidates: candidates.length });
  }, [active, candidates.length]);

  if (!active) return null;

  return (
    <div className={`flex items-center ${className}`} data-ribbon-slot={active}>
      {active === "story_arc" && (
        <StoryArcSurface ctx={ctx} minute={minute} className="flex-1" />
      )}
      {active === "nation_rivalry" && (
        <NationRivalryMomentsSurface className="flex-1" />
      )}
      {active === "world_status" && (
        <WorldStatusSurface className="flex-1" />
      )}
    </div>
  );
}